import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from 'app/hooks';
import { PageLoader } from 'components/Spin';
import {
  selectBrandCode,
  selectIsBrandDetailsExist,
} from 'services/brand/brandSelectors';

type Props = {
  children: ReactNode;
  /** Brand codes allowed to reach the route. Declare the list in a config module, not inline. */
  brandCodes: string[];
};

/**
 * Restricts a route to specific brands — identityWeb's per-brand route guards
 * (home-title-brand-guard.service.ts, financial-shield-brand-guard.service.ts), which gate the
 * single-page enrollment screens each partner owns.
 *
 * The brand is fetched asynchronously and its slice is persisted with a default, so the check
 * waits for `selectIsBrandDetailsExist` — the predicate that also means "this brand belongs to
 * this host". Without it the guard reads a stale code on a cold load and redirects a member who
 * was, in fact, allowed through.
 *
 * Rejections go to sign-in, following `HomeTitleBrandGuard`. identityWeb's Financial Shield guard
 * returns false without redirecting, which cancels navigation and leaves the member on a blank
 * shell; both brands redirect here.
 */
function BrandRoute({ children, brandCodes }: Props) {
  const isBrandDetailsExist = useAppSelector(selectIsBrandDetailsExist);
  const brandCode = useAppSelector(selectBrandCode);

  if (!isBrandDetailsExist) {
    return <PageLoader />;
  }

  if (!brandCodes.includes(brandCode)) {
    return <Navigate to='/sign-in' replace />;
  }

  return children;
}

export default BrandRoute;
